import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartDataPoint } from './types';

const DEFAULT_COLORS = ['#10b981', '#f59e0b', '#6366f1', '#ef4444', '#06b6d4', '#8b5cf6'];

interface DonutChartProps {
  data: ChartDataPoint[];
}

/**
 * Donut chart with a center label showing the first segment's percentage of the total.
 * Renders `'—'` in the center when total is zero. Fills its parent container.
 *
 * @remarks
 * The center label always displays the first segment's share. Only use this chart
 * when the first series item represents the primary metric to emphasize.
 *
 * @example
 * <DonutChart
 *   data={[{ name: 'Completed', value: 75 }, { name: 'Remaining', value: 25 }]}
 * />
 */
export function DonutChart({ data }: DonutChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="45%"
          innerRadius="52%"
          outerRadius="82%"
        >
          {data.map((entry, index) => (
            <Cell
              key={entry.name}
              fill={entry.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
            />
          ))}
        </Pie>
        <text
          x="50%"
          y="45%"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: '1.1rem', fontWeight: 700 }}
        >
          {total > 0 ? `${Math.round(((data[0]?.value ?? 0) / total) * 100)}%` : '—'}
        </text>
        <Tooltip
          formatter={(value) =>
            typeof value === 'number' ? value.toLocaleString() : String(value ?? '')
          }
        />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}
